import React, { useState, useImperativeHandle, forwardRef } from "react";
import {
  TextField,
  IconButton,
  MenuItem,
  Checkbox,
  Grid,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import SupplyItemSelector from "./SupplyItemSelector";

// Replaced confusing "unit" with "each"
const unitOptions = ["unit", "bag", "case", "pack", "each", "g", "kg"];
const qtyOptions = Array.from({ length: 20 }, (_, i) => i + 1);

const ShoppingItemRow = forwardRef(
  ({ item, onChange, onRemove, autoExpand, supplyOptions }, ref) => {
    const [showNote, setShowNote] = useState(!!item.note);
    const [expanded, setExpanded] = useState(autoExpand ?? true);

    useImperativeHandle(ref, () => ({
      collapse: () => setExpanded(false),
    }));

    const handleSelectorChange = (selectedItem) => {
      if (!selectedItem) return;

      const base = {
        name: selectedItem.name || selectedItem.label || "",
        type: selectedItem.type || "custom",
        quantity: item.quantity || 1,
        store: selectedItem.store || null,
        unit: "", // don't auto-set unit
      };

      if (selectedItem.type === "ingredient") {
        onChange({
          ...base,
          ingredient: selectedItem,
          supplyItem: null,
        });
      } else if (selectedItem.type === "supplyItem") {
        onChange({
          ...base,
          supplyItem: selectedItem,
          ingredient: null,
        });
      } else {
        onChange({
          ...base,
          supplyItem: null,
          ingredient: null,
        });
      }
    };

    const displayLabel = item.name || "(No item)";
    const displayQty = item.quantity || 1;
    const displayUnit = item.unit || ""; // don't fallback to "unit"

    return (
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            "& .MuiAccordionSummary-content": {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pr: 5,
            },
            position: "relative",
          }}>
          <Typography variant="body1" fontWeight={500}>
            {displayLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {displayUnit ? `${displayQty} × ${displayUnit}` : `${displayQty}`}
          </Typography>
        </AccordionSummary>

        <IconButton
          onClick={onRemove}
          size="small"
          sx={{
            position: "absolute",
            right: 8,
            top: 18,
            zIndex: 2,
            backgroundColor: "white",
          }}>
          <DeleteIcon fontSize="small" />
        </IconButton>

        <AccordionDetails>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <SupplyItemSelector
                selectedItem={item.supplyItem || item.ingredient || null}
                onSelect={handleSelectorChange}
                options={supplyOptions}
              />
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                label="Qty"
                value={item.quantity || ""}
                onChange={(e) => onChange({ quantity: e.target.value })}
                size="small"
                select
                fullWidth
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: { maxHeight: 200 },
                    },
                  },
                }}>
                {qtyOptions.map((qty) => (
                  <MenuItem key={qty} value={qty}>
                    {qty}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                label="Measurement Unit"
                value={item.unit || ""}
                onChange={(e) => onChange({ unit: e.target.value })}
                size="small"
                select
                fullWidth
                required
                error={!item.unit}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: { maxHeight: 300 },
                    },
                  },
                }}
                FormHelperTextProps={{
                  sx: { minHeight: "1.4em" },
                }}>
                {unitOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Checkbox
                  checked={showNote}
                  onChange={(e) => setShowNote(e.target.checked)}
                />
                <TextField
                  label="Note"
                  value={item.note || ""}
                  onChange={(e) => onChange({ note: e.target.value })}
                  size="small"
                  fullWidth
                  disabled={!showNote}
                />
              </Stack>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  }
);

export default ShoppingItemRow;
